import { useState, useEffect, useCallback } from "react";
import { 
  Brain, RefreshCw, Plus, Trash2, Download, Upload, 
  Check, AlertTriangle, Users, Wrench, Calendar,
  ChevronDown, ChevronUp, Sparkles, Building2, X, 
  Mic, Search, MapPin, Edit2, Save
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const ChristelleConfigPanel = () => {
  const [loading, setLoading] = useState(true);
  const [knowledge, setKnowledge] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);
  
  // États pour les ajouts
  const [addingItem, setAddingItem] = useState(null); // technicien, architecte, equipe
  const [newItemName, setNewItemName] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  
  // États pour les types RDV
  const [showAddTypeRdv, setShowAddTypeRdv] = useState(false);
  const [newTypeRdv, setNewTypeRdv] = useState({
    id: "",
    label: "",
    description: "",
    intervenant_type: "technicien"
  });
  
  // États pour les colonnes
  const [editingColonne, setEditingColonne] = useState(null);
  const [colonneValue, setColonneValue] = useState("");
  
  // États pour les prompts
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptValue, setPromptValue] = useState("");

  const loadKnowledge = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/knowledge`);
      if (resp.ok) {
        const data = await resp.json();
        setKnowledge(data);
      }
    } catch (e) {
      console.error("Erreur chargement connaissances Christelle:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadKnowledge();
  }, [loadKnowledge]);

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - TECHNICIENS
  // ═══════════════════════════════════════════════════════════════
  const handleAddTechnicien = async () => {
    if (!newItemName.trim()) return;
    setSavingItem(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/technicien`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom: newItemName })
      });
      if (resp.ok) {
        setNewItemName("");
        setAddingItem(null);
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur ajout technicien:", e);
    }
    setSavingItem(false);
  };

  const handleDeleteTechnicien = async (nom) => {
    if (!window.confirm(`Supprimer le technicien "${nom}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/technicien/${encodeURIComponent(nom)}`, {
        method: "DELETE"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression technicien:", e);
    }
  };

  const handleToggleTechnicien = async (nom) => {
    try {
      const resp = await authFetch(`${API}/christelle/config/technicien/${encodeURIComponent(nom)}/toggle`, {
        method: "POST"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur toggle technicien:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - ARCHITECTES
  // ═══════════════════════════════════════════════════════════════
  const handleAddArchitecte = async () => {
    if (!newItemName.trim()) return;
    setSavingItem(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/architecte`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom: newItemName })
      });
      if (resp.ok) {
        setNewItemName("");
        setAddingItem(null);
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur ajout architecte:", e);
    }
    setSavingItem(false);
  };

  const handleDeleteArchitecte = async (nom) => {
    if (!window.confirm(`Supprimer l'architecte "${nom}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/architecte/${encodeURIComponent(nom)}`, {
        method: "DELETE"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression architecte:", e);
    }
  };

  const handleToggleArchitecte = async (nom) => {
    try {
      const resp = await authFetch(`${API}/christelle/config/architecte/${encodeURIComponent(nom)}/toggle`, {
        method: "POST"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur toggle architecte:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - EQUIPES
  // ═══════════════════════════════════════════════════════════════
  const handleAddEquipe = async () => {
    if (!newItemName.trim()) return;
    setSavingItem(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/equipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom: newItemName })
      });
      if (resp.ok) {
        setNewItemName("");
        setAddingItem(null);
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur ajout équipe:", e);
    }
    setSavingItem(false);
  };

  const handleDeleteEquipe = async (nom) => {
    if (!window.confirm(`Supprimer l'équipe "${nom}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/equipe/${encodeURIComponent(nom)}`, {
        method: "DELETE"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression équipe:", e);
    }
  };

  const handleToggleEquipe = async (nom) => {
    try {
      const resp = await authFetch(`${API}/christelle/config/equipe/${encodeURIComponent(nom)}/toggle`, {
        method: "POST"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur toggle équipe:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - TYPES RDV
  // ═══════════════════════════════════════════════════════════════
  const handleAddTypeRdv = async () => {
    if (!newTypeRdv.id || !newTypeRdv.label) return;
    setSavingItem(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/type-rdv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newTypeRdv)
      });
      if (resp.ok) {
        setNewTypeRdv({ id: "", label: "", description: "", intervenant_type: "technicien" });
        setShowAddTypeRdv(false);
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur ajout type RDV:", e);
    }
    setSavingItem(false);
  };

  const handleDeleteTypeRdv = async (typeId) => {
    if (!window.confirm(`Supprimer le type de RDV "${typeId}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/type-rdv/${encodeURIComponent(typeId)}`, {
        method: "DELETE"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression type RDV:", e);
    }
  };

  const handleToggleTypeRdv = async (typeId) => {
    try {
      const resp = await authFetch(`${API}/christelle/config/type-rdv/${encodeURIComponent(typeId)}/toggle`, {
        method: "POST"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur toggle type RDV:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - COLONNES
  // ═══════════════════════════════════════════════════════════════
  const handleUpdateColonne = async (nomColonne) => {
    const indice = parseInt(colonneValue);
    if (isNaN(indice)) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/colonne`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom_colonne: nomColonne, indice })
      });
      if (resp.ok) {
        setEditingColonne(null);
        setColonneValue("");
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur mise à jour colonne:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - PROMPTS
  // ═══════════════════════════════════════════════════════════════
  const handleUpdatePrompt = async (nomPrompt) => {
    if (!promptValue.trim()) return;
    try {
      const resp = await authFetch(`${API}/christelle/config/prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom_prompt: nomPrompt, contenu: promptValue })
      });
      if (resp.ok) {
        setEditingPrompt(null);
        setPromptValue("");
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur mise à jour prompt:", e);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS - EXPORT / IMPORT
  // ═══════════════════════════════════════════════════════════════
  const handleExport = async () => {
    try {
      const resp = await authFetch(`${API}/christelle/config/export`);
      if (resp.ok) {
        const data = await resp.json();
        const blob = new Blob([data.data], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Erreur export:", e);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) return;
    setImporting(true);
    try {
      const resp = await authFetch(`${API}/christelle/config/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ json_data: importData, merge: true })
      });
      if (resp.ok) {
        const data = await resp.json();
        alert(`Import réussi !\n- ${data.imported.techniciens} techniciens\n- ${data.imported.architectes} architectes\n- ${data.imported.equipes} équipes\n- ${data.imported.types_rdv} types de RDV`);
        setShowImportModal(false);
        setImportData("");
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur import:", e);
      alert("Erreur lors de l'import");
    }
    setImporting(false);
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-pink-400" />
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div className="text-center py-12 text-slate-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
        <p>Impossible de charger les données</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <Wrench className="w-8 h-8 mx-auto mb-2 text-blue-400" />
          <p className="text-2xl font-bold text-blue-400">{knowledge.stats?.nb_techniciens || 0}</p>
          <p className="text-xs text-slate-400">Techniciens R1</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
          <Building2 className="w-8 h-8 mx-auto mb-2 text-purple-400" />
          <p className="text-2xl font-bold text-purple-400">{knowledge.stats?.nb_architectes || 0}</p>
          <p className="text-xs text-slate-400">Architectes R2</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
          <Users className="w-8 h-8 mx-auto mb-2 text-amber-400" />
          <p className="text-2xl font-bold text-amber-400">{knowledge.stats?.nb_equipes || 0}</p>
          <p className="text-xs text-slate-400">Équipes Chantier</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          <p className="text-2xl font-bold text-emerald-400">{knowledge.stats?.nb_types_rdv || 0}</p>
          <p className="text-xs text-slate-400">Types de RDV</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "overview", label: "Vue d'ensemble", icon: Brain },
          { id: "techniciens", label: "Techniciens R1", icon: Wrench },
          { id: "architectes", label: "Architectes R2", icon: Building2 },
          { id: "equipes", label: "Équipes Chantier", icon: Users },
          { id: "types-rdv", label: "Types de RDV", icon: Calendar },
          { id: "colonnes", label: "Mapping Colonnes", icon: MapPin },
          { id: "prompts", label: "Prompts", icon: Mic },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? "bg-pink-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        
        {/* Boutons d'action */}
        <div className="ml-auto flex gap-2">
          <button
            onClick={handleExport}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center gap-2"
            title="Exporter pour déploiement"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2"
            title="Importer des configurations"
          >
            <Upload className="w-4 h-4" />
            Importer
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET VUE D'ENSEMBLE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-6">
            <h3 className="font-semibold text-pink-400 mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Christelle Moisi - Assistante Recherche & Planification IA
            </h3>
            <p className="text-slate-300 mb-4">
              Christelle peut rechercher dans toutes les données clients, créer des rendez-vous par commande vocale, 
              et s'adapter à votre équipe grâce à cette configuration.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">Ce que Christelle sait faire :</h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Rechercher des clients par nom, téléphone, ville...</li>
                  <li>• Trouver des informations dans les appels et SMS</li>
                  <li>• Créer des RDV par commande vocale</li>
                  <li>• Assigner automatiquement les bons intervenants</li>
                  <li>• Répondre vocalement aux questions</li>
                </ul>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">Configuration actuelle :</h4>
                <div className="text-sm text-slate-400 space-y-2">
                  <p><span className="text-blue-400">{knowledge.stats?.nb_techniciens}</span> techniciens pour les R1</p>
                  <p><span className="text-purple-400">{knowledge.stats?.nb_architectes}</span> architectes pour les R2</p>
                  <p><span className="text-amber-400">{knowledge.stats?.nb_equipes}</span> équipes de pose</p>
                  <p><span className="text-emerald-400">{knowledge.stats?.nb_types_rdv}</span> types de RDV configurés</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Info persistance */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Persistance des données
            </h4>
            <p className="text-sm text-slate-400">
              Avant un déploiement, pensez à <strong className="text-amber-300">exporter</strong> la configuration de Christelle. 
              Vous pourrez ensuite l'<strong className="text-amber-300">importer</strong> dans le nouvel environnement.
            </p>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET TECHNICIENS R1 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "techniciens" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-400" />
              Techniciens R1 ({knowledge.techniciens_r1?.length || 0})
            </h3>
            <button
              onClick={() => setAddingItem(addingItem === "technicien" ? null : "technicien")}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          
          {addingItem === "technicien" && (
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4 flex gap-2">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Nom du technicien..."
                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddTechnicien()}
                autoFocus
              />
              <button
                onClick={handleAddTechnicien}
                disabled={savingItem || !newItemName.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm"
              >
                {savingItem ? "..." : "Ajouter"}
              </button>
              <button
                onClick={() => { setAddingItem(null); setNewItemName(""); }}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="space-y-2">
            {knowledge.techniciens_r1?.map((tech, i) => (
              <div 
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  tech.actif !== false ? "bg-slate-900/50" : "bg-slate-900/20 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${tech.actif !== false ? "bg-green-500" : "bg-slate-500"}`} />
                  <span className="font-medium">{tech.nom}</span>
                  {tech.actif === false && <span className="text-xs text-slate-500">(inactif)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleTechnicien(tech.nom)}
                    className={`px-3 py-1 rounded text-xs ${
                      tech.actif !== false 
                        ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" 
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    }`}
                  >
                    {tech.actif !== false ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    onClick={() => handleDeleteTechnicien(tech.nom)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {(!knowledge.techniciens_r1 || knowledge.techniciens_r1.length === 0) && (
              <p className="text-center py-8 text-slate-500">Aucun technicien configuré</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET ARCHITECTES R2 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "architectes" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Architectes R2 ({knowledge.architectes_r2?.length || 0})
            </h3>
            <button
              onClick={() => setAddingItem(addingItem === "architecte" ? null : "architecte")}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          
          {addingItem === "architecte" && (
            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg mb-4 flex gap-2">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Nom de l'architecte..."
                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddArchitecte()}
                autoFocus
              />
              <button
                onClick={handleAddArchitecte}
                disabled={savingItem || !newItemName.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg text-sm"
              >
                {savingItem ? "..." : "Ajouter"}
              </button>
              <button
                onClick={() => { setAddingItem(null); setNewItemName(""); }}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="space-y-2">
            {knowledge.architectes_r2?.map((arch, i) => (
              <div 
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  arch.actif !== false ? "bg-slate-900/50" : "bg-slate-900/20 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${arch.actif !== false ? "bg-green-500" : "bg-slate-500"}`} />
                  <span className="font-medium">{arch.nom}</span>
                  {arch.actif === false && <span className="text-xs text-slate-500">(inactif)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleArchitecte(arch.nom)}
                    className={`px-3 py-1 rounded text-xs ${
                      arch.actif !== false 
                        ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" 
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    }`}
                  >
                    {arch.actif !== false ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    onClick={() => handleDeleteArchitecte(arch.nom)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {(!knowledge.architectes_r2 || knowledge.architectes_r2.length === 0) && (
              <p className="text-center py-8 text-slate-500">Aucun architecte configuré</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET ÉQUIPES CHANTIER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "equipes" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-400" />
              Équipes Chantier ({knowledge.equipes_chantier?.length || 0})
            </h3>
            <button
              onClick={() => setAddingItem(addingItem === "equipe" ? null : "equipe")}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          
          {addingItem === "equipe" && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4 flex gap-2">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Nom de l'équipe..."
                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddEquipe()}
                autoFocus
              />
              <button
                onClick={handleAddEquipe}
                disabled={savingItem || !newItemName.trim()}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg text-sm"
              >
                {savingItem ? "..." : "Ajouter"}
              </button>
              <button
                onClick={() => { setAddingItem(null); setNewItemName(""); }}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="space-y-2">
            {knowledge.equipes_chantier?.map((equipe, i) => (
              <div 
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  equipe.actif !== false ? "bg-slate-900/50" : "bg-slate-900/20 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${equipe.actif !== false ? "bg-green-500" : "bg-slate-500"}`} />
                  <span className="font-medium">{equipe.nom}</span>
                  {equipe.actif === false && <span className="text-xs text-slate-500">(inactif)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleEquipe(equipe.nom)}
                    className={`px-3 py-1 rounded text-xs ${
                      equipe.actif !== false 
                        ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" 
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    }`}
                  >
                    {equipe.actif !== false ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    onClick={() => handleDeleteEquipe(equipe.nom)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {(!knowledge.equipes_chantier || knowledge.equipes_chantier.length === 0) && (
              <p className="text-center py-8 text-slate-500">Aucune équipe configurée</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET TYPES DE RDV */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "types-rdv" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-400" />
              Types de RDV ({knowledge.types_rdv?.length || 0})
            </h3>
            <button
              onClick={() => setShowAddTypeRdv(!showAddTypeRdv)}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          
          {showAddTypeRdv && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newTypeRdv.id}
                  onChange={(e) => setNewTypeRdv({ ...newTypeRdv, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="ID (ex: r3, sav...)"
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={newTypeRdv.label}
                  onChange={(e) => setNewTypeRdv({ ...newTypeRdv, label: e.target.value })}
                  placeholder="Label (ex: R3 - Signature)"
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <input
                type="text"
                value={newTypeRdv.description}
                onChange={(e) => setNewTypeRdv({ ...newTypeRdv, description: e.target.value })}
                placeholder="Description..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex gap-3">
                <select
                  value={newTypeRdv.intervenant_type}
                  onChange={(e) => setNewTypeRdv({ ...newTypeRdv, intervenant_type: e.target.value })}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="technicien">Technicien</option>
                  <option value="architecte">Architecte</option>
                  <option value="equipe">Équipe chantier</option>
                </select>
                <button
                  onClick={handleAddTypeRdv}
                  disabled={savingItem || !newTypeRdv.id || !newTypeRdv.label}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm"
                >
                  {savingItem ? "..." : "Ajouter"}
                </button>
                <button
                  onClick={() => { setShowAddTypeRdv(false); setNewTypeRdv({ id: "", label: "", description: "", intervenant_type: "technicien" }); }}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            {knowledge.types_rdv?.map((type, i) => (
              <div 
                key={i}
                className={`flex items-center justify-between p-4 rounded-lg ${
                  type.actif !== false ? "bg-slate-900/50" : "bg-slate-900/20 opacity-60"
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-2 py-0.5 bg-slate-700 rounded text-xs font-mono">{type.id}</span>
                    <span className="font-medium">{type.label}</span>
                    {type.actif === false && <span className="text-xs text-slate-500">(inactif)</span>}
                  </div>
                  <p className="text-sm text-slate-400">{type.description}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Intervenant: <span className={
                      type.intervenant_type === "technicien" ? "text-blue-400" :
                      type.intervenant_type === "architecte" ? "text-purple-400" :
                      "text-amber-400"
                    }>{type.intervenant_type}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleTypeRdv(type.id)}
                    className={`px-3 py-1 rounded text-xs ${
                      type.actif !== false 
                        ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" 
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    }`}
                  >
                    {type.actif !== false ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    onClick={() => handleDeleteTypeRdv(type.id)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {(!knowledge.types_rdv || knowledge.types_rdv.length === 0) && (
              <p className="text-center py-8 text-slate-500">Aucun type de RDV configuré</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET MAPPING COLONNES */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "colonnes" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-cyan-400" />
            Mapping des colonnes Google Sheet
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            Ces indices correspondent aux colonnes du Google Sheet où Christelle écrit les données de RDV.
            <strong className="text-amber-400"> Modifiez uniquement si vous savez ce que vous faites.</strong>
          </p>
          
          <div className="space-y-2">
            {Object.entries(knowledge.colonnes_sheet || {}).map(([nom, indice], i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-slate-400">{nom}</span>
                </div>
                {editingColonne === nom ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={colonneValue}
                      onChange={(e) => setColonneValue(e.target.value)}
                      className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-center"
                      autoFocus
                    />
                    <button
                      onClick={() => handleUpdateColonne(nom)}
                      className="p-1.5 bg-emerald-600 hover:bg-emerald-700 rounded"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setEditingColonne(null); setColonneValue(""); }}
                      className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded font-mono text-sm">{indice}</span>
                    <button
                      onClick={() => { setEditingColonne(nom); setColonneValue(String(indice)); }}
                      className="p-1.5 hover:bg-slate-700 rounded text-slate-400"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET PROMPTS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "prompts" && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Mic className="w-5 h-5 text-pink-400" />
            Prompts personnalisés
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            Ces phrases sont utilisées par Christelle lors des interactions vocales.
          </p>
          
          <div className="space-y-3">
            {Object.entries(knowledge.prompts_personnalises || {}).map(([nom, contenu], i) => (
              <div key={i} className="p-4 bg-slate-900/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm text-pink-400">{nom}</span>
                  {editingPrompt !== nom && (
                    <button
                      onClick={() => { setEditingPrompt(nom); setPromptValue(contenu); }}
                      className="p-1.5 hover:bg-slate-700 rounded text-slate-400"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {editingPrompt === nom ? (
                  <div className="space-y-2">
                    <textarea
                      value={promptValue}
                      onChange={(e) => setPromptValue(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setEditingPrompt(null); setPromptValue(""); }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-sm"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => handleUpdatePrompt(nom)}
                        className="px-3 py-1.5 bg-pink-600 hover:bg-pink-700 rounded text-sm flex items-center gap-1"
                      >
                        <Save className="w-4 h-4" />
                        Sauvegarder
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-300">{contenu}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MODAL IMPORT */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-400" />
                Importer une configuration
              </h3>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-400 mb-4">
                Collez le contenu JSON exporté précédemment pour restaurer la configuration de Christelle.
              </p>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder='{"knowledge": {...}}'
                className="w-full h-64 bg-slate-800 border border-slate-700 rounded-lg p-4 font-mono text-sm"
              />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !importData.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg flex items-center gap-2"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Importer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChristelleConfigPanel;
